/*
    Copyright 2022-2026, Techwise Integrated Security, LLC
*/

import React, { useEffect, useState } from "react";

import {
    Autocomplete,
    Backdrop,
    Box,
    Button,
    CircularProgress,
    Fade,
    Grid,
    IconButton,
    Modal,
    Stack,
    TextField,
    Tooltip,
    Typography,
    useTheme,
} from "@mui/material";

import { AnyAction, Dispatch } from "@reduxjs/toolkit";
import { AssetWithTransfer, DetailedUser, Effect, ModalBase, Nullable, User } from "../../utils/Interfaces";
import { delayedAction } from "../../utils/Functions";
import CloseIcon from "@mui/icons-material/Close";
import { selectExistingUsers } from "../admin/user-management/userManagementSlice";
import { refreshUsers } from "../admin/user-management/userManagementFunctions";
import { createAssetTransfer } from "./assetTransferFunctions";
import { selectId } from "../auth/authSlice";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { useWindowDimensions } from "../../utils/Hooks";

export interface InitiateAssetTransferModalProps extends ModalBase {
    asset: AssetWithTransfer;
    onAssetTransferCreated: Effect;
}

const InitiateAssetTransferModal = (props: InitiateAssetTransferModalProps) => {
    const { open, handleClose, asset, onAssetTransferCreated } = props;

    const dispatch: Dispatch<AnyAction> = useAppDispatch();
    const theme = useTheme();

    const { width } = useWindowDimensions();
    const mobile: boolean = width <= 600;

    const [loading, setLoading] = useState<boolean>(false);
    const [selectedUser, setSelectedUser] = useState<Nullable<User>>(null);

    const [usersLoaded, setUsersLoaded] = useState<boolean>(false);

    const existingUsers: DetailedUser[] = useAppSelector(selectExistingUsers);
    const currentUserId: number = useAppSelector(selectId);

    const [notes, setNotes] = useState<string>("");

    useEffect(() => {
        if (!usersLoaded) {
            refreshUsers(dispatch, () => {});
            setUsersLoaded(true);
        }
    }, [open]);

    function getFormErrors(): string {
        if (!selectedUser) {
            return "Please select a user to continue.";
        }

        if (notes.length < 1) {
            return "Please provide additional notes on why the asset is being transferred.";
        }

        return "";
    }

    function clearAssetValues(): void {
        setSelectedUser(null);
        setNotes("");
        setLoading(false);
    }

    function onClose() {
        handleClose();
        void delayedAction(500, () => {
            clearAssetValues();
        });
    }

    if (!asset) {
        return <Box></Box>;
    }

    return (
        <Modal
            sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
            }}
            open={open}
            onClose={onClose}
            closeAfterTransition
            BackdropComponent={Backdrop}
            BackdropProps={{
                timeout: 500,
            }}
        >
            <Fade in={open}>
                <Box
                    sx={{
                        backgroundColor: theme.palette.background.paper,
                        border: "1px solid #000",
                        boxShadow: theme.shadows[5],
                        padding: theme.spacing(2, 4, 3),
                        zIndex: 1000,
                        width: mobile ? "95vw" : "60vw",
                    }}
                >
                    <Box>
                        <Stack direction="row" justifyContent="space-between" sx={{ mb: 2 }}>
                            <Typography variant="h5" textAlign="center" sx={{ ml: 2, mb: 2, width: "100%" }}>
                                {`Initiate Transfer for ${asset.name}`}
                            </Typography>
                            <Stack direction="column" justifyContent="flex-start" sx={{ mr: -2 }}>
                                <IconButton aria-label="delete" onClick={onClose}>
                                    <CloseIcon />
                                </IconButton>
                            </Stack>
                        </Stack>

                        <Grid container>
                            <Grid xs={12} item>
                                <Autocomplete
                                    disablePortal
                                    onChange={(_event, value) => {
                                        setSelectedUser(value ? value : null);
                                    }}
                                    sx={{ mt: 2 }}
                                    id="selected_user"
                                    getOptionKey={(option: User) => option.id}
                                    options={existingUsers.filter((user: User) => user.id !== currentUserId)}
                                    value={selectedUser}
                                    getOptionLabel={(option: User): string => `${option.first_name} ${option.last_name}`}
                                    renderInput={(params) => <TextField required {...params} label="Selected User" />}
                                />
                            </Grid>
                            <Grid xs={12} item>
                                <TextField
                                    sx={{ mt: 3 }}
                                    disabled={loading}
                                    multiline
                                    rows={4}
                                    fullWidth
                                    margin="normal"
                                    id="notes"
                                    name="notes"
                                    value={notes}
                                    label="Transfer Reason/Notes"
                                    size="small"
                                    onChange={(event: React.ChangeEvent<HTMLInputElement>) => setNotes(event.target.value)}
                                />
                            </Grid>
                        </Grid>

                        <Box sx={{ mb: 5 }} />

                        {loading && (
                            <Stack direction="row" justifyContent="center">
                                <CircularProgress size={16} sx={{ mt: 1.25, mb: 1.25 }} />
                            </Stack>
                        )}

                        {!loading && (
                            <Stack direction="row" justifyContent="center" sx={{ mt: 2 }}>
                                <Tooltip title="Discard changes">
                                    <Button variant="contained" color="secondary" sx={{ mr: 1 }} onClick={onClose}>
                                        Cancel
                                    </Button>
                                </Tooltip>
                                <Tooltip title="Update material">
                                    <Button
                                        disabled={getFormErrors() !== ""}
                                        variant="contained"
                                        color="success"
                                        sx={{ ml: 1 }}
                                        onClick={() => {
                                            setLoading(true);
                                            createAssetTransfer(
                                                asset.id,
                                                selectedUser?.id ?? 0,
                                                notes,
                                                dispatch,
                                                () => {
                                                    onAssetTransferCreated();
                                                    onClose();
                                                },
                                                () => {}
                                            );
                                        }}
                                    >
                                        Initiate Transfer
                                    </Button>
                                </Tooltip>
                            </Stack>
                        )}
                    </Box>
                </Box>
            </Fade>
        </Modal>
    );
};

export default InitiateAssetTransferModal;
